// Resource types and visibility, matching trust_center.resources.
export const RESOURCE_TYPES = {
  knowledge_base: { label: "From Regodit", short: "Regodit" },
  document: { label: "Uploaded file", short: "File" },
  link: { label: "Link", short: "Link" },
};

// Requestable content is locked until an admin accepts the request and
// emails the document. Email only content is listed but never opened on the
// page: the visitor types an email and the file is sent to it.
//   listed:   shown in the Resources lists on the public page
//   gated:    visitor must request access; you grant from Access requests
//   emailed:  sent to the visitor's email, no code and no approval
export const VISIBILITY = {
  public: {
    label: "Public",
    help: "Shown on your Trust Center. Anyone who can see the page can open it.",
    listed: true,
    gated: false,
  },
  requestable: {
    label: "Requestable",
    help: "Shown with a lock. Visitors confirm their email with a code, then you accept or decline the request. Accept emails them the document.",
    listed: true,
    gated: true,
  },
  email: {
    label: "Email only",
    help: "Shown on your Trust Center, but it cannot be opened or downloaded there. Visitors enter their email and the file is emailed to them right away, with no approval.",
    listed: true,
    gated: false,
    emailed: true,
  },
  private: {
    label: "Private",
    help: "Not shown on the Trust Center and cannot be shared with visitors.",
    listed: false,
    gated: true,
  },
};

// Links are always public (enforced by the database too).
export const visibilityOptionsFor = (type) =>
  type === "link" ? ["public"] : ["public", "requestable", "email", "private"];

// Older names. Shareable was retired; those files are requestable.
export const OLD_VISIBILITY = {
  restricted: "requestable",
  hidden: "private",
  shareable: "requestable",
};
export const normalizeVisibility = (value) => OLD_VISIBILITY[value] || value;

export const isListed = (resource) => !!VISIBILITY[resource.visibility]?.listed;
export const isGated = (resource) => !!VISIBILITY[resource.visibility]?.gated;
// Only ever sent to the visitor's email (never opened on the page).
export const isEmailed = (resource) =>
  !!VISIBILITY[resource.visibility]?.emailed;

// What a visitor may open: approval is per document. `access` is a Set of the
// resource ids an admin approved for this visitor, or `true` (an older API
// that only said "approved for something": every locked file).
export const accessOf = (visitor, session) => {
  const lists = [
    visitor?.grantedResourceIds,
    session?.grantedResourceIds,
  ].filter(Array.isArray);
  if (lists.length) return new Set(lists.flat().map(Number));
  return visitor?.privateAccess || session?.privateAccess ? true : new Set();
};

// Whether the visitor may open this resource now: public ones always, locked
// ones once approved for that document.
export const isUnlocked = (resource, access) =>
  !isGated(resource) ||
  access === true ||
  (access instanceof Set && access.has(Number(resource.id)));

// "Uploaded · SOC 2.pdf", "From Regodit · Policy", "Link · acme.com/privacy"
export const resourceSource = (resource) => {
  if (resource.type === "knowledge_base")
    return `From Regodit · ${resource.sourceLabel || "Policies & Documents"}`;
  if (resource.type === "link")
    return `Link · ${String(resource.url || "").replace(/^https?:\/\//, "")}`;
  return `Uploaded · ${resource.fileName || "file"}`;
};

// Resources grouped by category, both in their saved order. Empty categories
// are kept when `keepEmpty` is set (the admin needs them; the public page not).
export const groupByCategory = (categories, resources, keepEmpty = false) =>
  categories
    .map((category) => ({
      ...category,
      resources: resources.filter((r) => r.categoryId === category.id),
    }))
    .filter((group) => keepEmpty || group.resources.length > 0);

// On the public Overview, each category shows this many resources.
export const OVERVIEW_PER_CATEGORY = 4;

// Moves the item with `id` to `toIndex` within the items that `inGroup` keeps,
// leaving every other item where it is. Used for drag-and-drop reordering.
export const moveWithin = (items, id, toIndex, inGroup = () => true) => {
  const group = items.filter(inGroup);
  const from = group.findIndex((item) => item.id === id);
  if (from === -1 || from === toIndex) return items;
  const reordered = [...group];
  const [moved] = reordered.splice(from, 1);
  reordered.splice(toIndex, 0, moved);
  let next = 0;
  return items.map((item) => (inGroup(item) ? reordered[next++] : item));
};

// The public link to one resource, for "Copy link".
export const resourceLink = (pageUrl, resourceId) =>
  `${pageUrl}/resources?resource=${resourceId}`;
